import type { PanelMode, Rect, SnapRegion } from './types';

/**
 * Which side, if any, the main dock should shrink AWAY from for a 50/50 split,
 * given the current panel modes. A side "wins" only when it holds snapped
 * panels and the opposite side does not; if BOTH columns (or neither) are
 * claimed the dock keeps its full width (the opaque snapped cards cover it).
 * A maximized panel's mode is never a `snapped-*`, so it never contributes.
 * Pure so it is unit-testable without the store/DOM. See DOCK_CONTRACT.md §4.1.
 */
export function splitSide(modes: Iterable<PanelMode>): 'left' | 'right' | 'none' {
  let left = false;
  let right = false;
  for (const m of modes) {
    if (m === 'snapped-left' || m === 'snapped-top-left' || m === 'snapped-bottom-left') left = true;
    else if (m === 'snapped-right' || m === 'snapped-top-right' || m === 'snapped-bottom-right') right = true;
  }
  if (left && !right) return 'left';
  if (right && !left) return 'right';
  return 'none';
}

export interface SnapMetrics {
  /** Half-edge band width (px) for left/right half snapping (middle of an edge). */
  edge: number;
  /** Corner hitbox width/height (px) — size-relative so it stays sane at any size. */
  cornerX: number;
  cornerY: number;
}

/**
 * Snap hitbox sizes, relative to the live workspace so corners stay reachable
 * whether the workspace is small or full-screen.
 */
export function snapMetrics(ws: Rect): SnapMetrics {
  return {
    edge: Math.min(56, ws.width * 0.1),
    cornerX: Math.min(150, ws.width * 0.3),
    cornerY: Math.min(150, ws.height * 0.34),
  };
}

/**
 * Detect a snap region from a pointer position in workspace-local coordinates.
 * Corners (quarters) take precedence over edges (halves); null → stays floating.
 */
export function detectSnap(px: number, py: number, ws: Rect): SnapRegion | null {
  const { edge, cornerX, cornerY } = snapMetrics(ws);
  const nearLeft = px <= cornerX;
  const nearRight = px >= ws.width - cornerX;
  const nearTop = py <= cornerY;
  const nearBottom = py >= ws.height - cornerY;

  // (1) corners beat edges
  if (nearBottom && nearLeft) return 'bottom-left';
  if (nearBottom && nearRight) return 'bottom-right';
  if (nearTop && nearLeft) return 'top-left';
  if (nearTop && nearRight) return 'top-right';

  // (2) half-edges (the middle band of a side)
  if (px <= edge) return 'left';
  if (px >= ws.width - edge) return 'right';
  return null;
}

/** Target rectangle for a snap region, always a percentage of the workspace. */
export function snapRect(region: SnapRegion, ws: Rect): Rect {
  const halfW = Math.round(ws.width / 2);
  const halfH = Math.round(ws.height / 2);
  const rightX = ws.width - halfW;
  const bottomY = ws.height - halfH;
  switch (region) {
    case 'left':
      return { x: 0, y: 0, width: halfW, height: ws.height };
    case 'right':
      return { x: rightX, y: 0, width: halfW, height: ws.height };
    case 'top-left':
      return { x: 0, y: 0, width: halfW, height: halfH };
    case 'top-right':
      return { x: rightX, y: 0, width: halfW, height: halfH };
    case 'bottom-left':
      return { x: 0, y: bottomY, width: halfW, height: halfH };
    case 'bottom-right':
      return { x: rightX, y: bottomY, width: halfW, height: halfH };
  }
}
